/**
 * Sky model (AD-651 item 2, q-tidewater-tier-a-techniques) — the PURE half of the
 * sky dome: the stylised default parameters, the sun direction taken from the
 * plan's directional light, and a CPU evaluation of three's Preetham sky so the
 * fog colour can be DERIVED from the sky's horizon instead of hand-set.
 *
 * Both renderer siblings draw three r185's own Preetham implementation —
 * `examples/jsm/objects/Sky.js` (GLSL, the WebGL2 default, `scene/sky.ts`) and
 * `examples/jsm/objects/SkyMesh.js` (TSL, the opt-in WebGPU path,
 * `scene/skyNodeMesh.ts`). `preethamRadiance()` below is a line-for-line port of
 * their shared vertex + fragment maths (clouds and the sun disc excluded), so the
 * horizon the fog is matched to IS the horizon the dome draws. If three changes
 * the model, `sky.test.ts` pins the GLSL constants this port relies on.
 *
 * Like plan.ts this imports NO `three` and does no I/O: node-testable.
 * Import boundary (AD-122): frontend-local.
 */

/** A unit direction (world space, +y up). */
export interface SkyVector {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** The tunable Preetham parameters — the same names as the Sky/SkyMesh uniforms. */
export interface SkyParams {
  /** Haze (aerosol) amount; low = clean, saturated blue. */
  readonly turbidity: number;
  /** Rayleigh (molecular) scattering multiplier; higher = deeper blue, warmer horizon. */
  readonly rayleigh: number;
  /** Mie (aerosol) scattering coefficient; the glow around the sun. */
  readonly mieCoefficient: number;
  /** Mie forward-scattering anisotropy (0..1); higher = tighter sun glow. */
  readonly mieDirectionalG: number;
  /**
   * Scene-linear multiplier on the dome's output, applied before the grade and tone
   * mapping (the three shaders have no exposure uniform; both siblings scale it).
   */
  readonly exposure: number;
  /**
   * The stylised GRADE on top of three's physical sky (AD-9006), identical in both
   * siblings and in `gradeSky()` below: chroma is scaled about luminance by
   * `saturation`, multiplied by `tint`, and luminance above `knee` is compressed
   * smoothly toward `ceiling`. With `ceiling` under BLOOM_DEFAULTS.threshold no sky
   * pixel can bloom, however bright Preetham's aureole gets near a low sun.
   */
  readonly saturation: number;
  readonly tint: readonly [number, number, number];
  readonly knee: number;
  readonly ceiling: number;
  /**
   * The sun disc — drawn by the dome's own composition AFTER the luminance cap and hidden
   * by cloud (AD-9007), replacing the stock disc (a physical ~10⁴×-sky sun that would
   * smear across the screen through the bloom). Angular diameter (degrees), scene-linear
   * luminance (what the bloom pass sees) and colour multiplier.
   */
  readonly sunDiscDegrees: number;
  readonly sunDiscLuminance: number;
  readonly sunDiscTint: readonly [number, number, number];
}

/**
 * The stylised default (Art Direction Bible §1–§3: "not realism"; Tier 1 "bright
 * mid-key … fresh greens & sky-blues"; sky/water anchor `#7fb6d4`), for the AD-9005
 * low sun (≈16.7°). AD-9006, after the owner's playtest ("washed out and bland";
 * "the sundisc lighting is extreme and covers a huge area"): the raw dome put 18 % of
 * the upper sky over the bloom threshold (peak 3.4 near the sun) — a white veil. The
 * grade lifts chroma toward a deeper blue, keeps the low sun's golden glow, and caps the
 * whole dome at 0.78 so ONLY the disc blooms.
 */
export const DEFAULT_SKY_PARAMS: SkyParams = {
  turbidity: 2,
  rayleigh: 3,
  mieCoefficient: 0.002,
  mieDirectionalG: 0.85,
  exposure: 0.45,
  saturation: 1.8,
  tint: [0.92, 0.96, 1.15],
  knee: 0.45,
  ceiling: 0.82,
  sunDiscDegrees: 1.6,
  sunDiscLuminance: 6,
  sunDiscTint: [1, 0.88, 0.65],
};

/**
 * Dome size in world metres. The dome is three's unit box scaled by this; its
 * vertex shader pins depth to the far plane (`z = w`), so the size only has to keep
 * the camera inside the box — the per-fragment view ray is exact wherever it sits.
 */
export const DEFAULT_SKY_RADIUS = 4000;

// --- Preetham constants, verbatim from three r185 objects/Sky.js ---------------------
const E = 2.718281828459045;
const TOTAL_RAYLEIGH: readonly [number, number, number] = [
  5.804542996261093e-6, 1.3562911419845635e-5, 3.0265902468824876e-5,
];
const MIE_CONST: readonly [number, number, number] = [
  1.8399918514433978e14, 2.7798023919660528e14, 4.0790479543861094e14,
];
const CUTOFF_ANGLE = 1.6110731556870734;
const STEEPNESS = 1.5;
const EE = 1000.0;
const RAYLEIGH_ZENITH_LENGTH = 8.4e3;
const MIE_ZENITH_LENGTH = 1.25e3;
const THREE_OVER_SIXTEENPI = 0.05968310365946075;
const ONE_OVER_FOURPI = 0.07957747154594767;

type Rgb = [number, number, number];

function normalize(v: SkyVector): SkyVector {
  const len = Math.hypot(v.x, v.y, v.z);
  if (!(len > 0)) return { x: 0, y: 1, z: 0 };
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}

/**
 * The sun direction the dome draws: from the scene origin toward the directional
 * light's position — exactly the direction three's `DirectionalLight` (target at the
 * origin) casts from, so the disc sits where the shadows say the sun is.
 */
export function sunDirectionFromLight(light: { x: number; y: number; z: number }): SkyVector {
  return normalize(light);
}

/**
 * Scene-linear radiance of three's Preetham sky in `direction` (unit, world space)
 * for a unit `sun` direction, scaled by `params.exposure`, BEFORE the grade. No clouds, no
 * sun disc — the sky gradient only. A faithful port of Sky.js's vertex + fragment maths.
 */
export function preethamRadiance(direction: SkyVector, sun: SkyVector, params: SkyParams): Rgb {
  return evaluate(direction, sun, params).sky;
}

/** Rec. 709 relative luminance of a linear colour (what UnrealBloomPass thresholds). */
export function luminance(rgb: readonly [number, number, number]): number {
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

function evaluate(direction: SkyVector, sun: SkyVector, params: SkyParams): { sky: Rgb } {
  const dir = normalize(direction);
  const s = normalize(sun);
  const up = { x: 0, y: 1, z: 0 };
  const dotV = (a: SkyVector, b: SkyVector): number => a.x * b.x + a.y * b.y + a.z * b.z;

  // Vertex stage (per-dome constants).
  const zenithCos = Math.min(1, Math.max(-1, dotV(s, up)));
  const sunE =
    EE * Math.max(0, 1 - Math.pow(E, -((CUTOFF_ANGLE - Math.acos(zenithCos)) / STEEPNESS)));
  const sunfade = 1 - Math.min(1, Math.max(0, 1 - Math.exp(s.y / 450000)));
  const rayleighCoefficient = params.rayleigh - (1 - sunfade);
  const betaR = TOTAL_RAYLEIGH.map((c) => c * rayleighCoefficient) as Rgb;
  const mieC = 0.2 * params.turbidity * 10e-18;
  const betaM = MIE_CONST.map((c) => 0.434 * mieC * c * params.mieCoefficient) as Rgb;

  // Fragment stage.
  const zenithAngle = Math.acos(Math.max(0, dotV(up, dir)));
  const inverse =
    1 / (Math.cos(zenithAngle) + 0.15 * Math.pow(93.885 - (zenithAngle * 180) / Math.PI, -1.253));
  const sR = RAYLEIGH_ZENITH_LENGTH * inverse;
  const sM = MIE_ZENITH_LENGTH * inverse;
  const cosTheta = dotV(dir, s);
  const rPhase = THREE_OVER_SIXTEENPI * (1 + Math.pow(cosTheta * 0.5 + 0.5, 2));
  const g = params.mieDirectionalG;
  const g2 = g * g;
  const mPhase = ONE_OVER_FOURPI * ((1 - g2) / Math.pow(1 - 2 * g * cosTheta + g2, 1.5));
  const horizonMix = Math.min(1, Math.max(0, Math.pow(1 - dotV(up, s), 5)));

  const out: Rgb = [0, 0, 0];
  const floorTint: Rgb = [0, 0.0003, 0.00075];
  for (let i = 0; i < 3; i += 1) {
    const fex = Math.exp(-(betaR[i]! * sR + betaM[i]! * sM));
    const ratio = (betaR[i]! * rPhase + betaM[i]! * mPhase) / (betaR[i]! + betaM[i]!);
    let lin = Math.pow(sunE * ratio * (1 - fex), 1.5);
    lin *= 1 + (Math.pow(sunE * ratio * fex, 0.5) - 1) * horizonMix;
    const l0 = 0.1 * fex;
    out[i] = ((lin + l0) * 0.04 + floorTint[i]!) * params.exposure;
  }
  return { sky: out };
}

/**
 * The stylised grade (AD-9006) — the CPU twin of the GLSL `skyGrade()` / `skyCap()` and
 * the TSL graph in skyNodeMesh.ts: saturation about luminance and a tint (`skyColourGrade`),
 * then a smooth compression of luminance above `knee` that approaches but never reaches
 * `ceiling` (`skyCap`). Clouds are composited BETWEEN the two (`skyWithClouds`), so their
 * authored colours are not re-saturated but ARE capped — no cloud can bloom either.
 */
export function skyColourGrade(rgb: readonly [number, number, number], params: SkyParams): Rgb {
  const l0 = luminance(rgb);
  return rgb.map((v, i) => Math.max(0, l0 + (v - l0) * params.saturation) * params.tint[i]!) as Rgb;
}

/** The soft luminance ceiling (see `skyColourGrade`). */
export function skyCap(rgb: readonly [number, number, number], params: SkyParams): Rgb {
  const l1 = luminance(rgb);
  if (!(l1 > params.knee)) return [rgb[0], rgb[1], rgb[2]];
  const span = params.ceiling - params.knee;
  const t = params.knee + span * (1 - Math.exp(-(l1 - params.knee) / span));
  return rgb.map((v) => (v * t) / l1) as Rgb;
}

/** Colour grade then cap — the clear sky (no clouds). */
export function gradeSky(rgb: readonly [number, number, number], params: SkyParams): Rgb {
  return skyCap(skyColourGrade(rgb, params), params);
}

/** What the dome draws in `direction`: the graded Preetham radiance (no disc). */
export function skyRadiance(direction: SkyVector, sun: SkyVector, params: SkyParams): Rgb {
  return gradeSky(preethamRadiance(direction, sun, params), params);
}

/**
 * The sky's horizon colour: `skyRadiance` (graded, as the dome draws it) at zero elevation, averaged over
 * `samples` evenly spaced azimuths (the fog is one colour; the sky's horizon varies
 * with the sun's bearing), in scene-linear units.
 */
export function horizonRadiance(sun: SkyVector, params: SkyParams, samples = 32): Rgb {
  const sum: Rgb = [0, 0, 0];
  for (let k = 0; k < samples; k += 1) {
    const a = (k / samples) * Math.PI * 2;
    const c = skyRadiance({ x: Math.cos(a), y: 0, z: Math.sin(a) }, sun, params);
    sum[0] += c[0];
    sum[1] += c[1];
    sum[2] += c[2];
  }
  return [sum[0] / samples, sum[1] / samples, sum[2] / samples];
}

// --- The cloud layer (AD-9007; Bible §8.5) ------------------------------------------

/**
 * The stylised weather-cloud layer, and the ONLY place The Great Cloud shows in the sky:
 * a faint opal iridescence on thin cloud edges near the sun (Bible §3.5 signature, §8.5).
 * The Great Cloud itself is never given a form (§5.3). Composited in the dome's own shader
 * (GLSL `skyClouds()` in sky.ts, the TSL graph in skyNodeMesh.ts; this is their CPU twin),
 * so it costs no extra draw.
 */
export interface CloudParams {
  /** Fraction of the sky under cloud, 0..1. */
  readonly coverage: number;
  /** Edge width of the coverage threshold (softer = wispier). */
  readonly softness: number;
  /** Noise frequency on the projected cloud plane (bigger = smaller clouds). */
  readonly scale: number;
  /** Peak opacity of a cloud's body, 0..1. */
  readonly opacity: number;
  /** Scene-linear colour of a sunlit cloud top, and of the self-shadowed underside. */
  readonly litColour: readonly [number, number, number];
  readonly shadeColour: readonly [number, number, number];
  /** Warm glow added on sun-facing thin edges (the low sun's gold lining). */
  readonly rimColour: readonly [number, number, number];
  /**
   * Strength of The Great Cloud-iridescence fringe (cyan → magenta → gold, §3.5) on thin edges
   * within ≈ 50° of the sun. The zone-tier seam: nearer The Great Cloud's reach, stronger.
   */
  readonly iridescence: number;
  /** Plane units per second the layer drifts (x, z); frozen by the `skystill` dev flag. */
  readonly drift: readonly [number, number];
  /** fbm octaves: 4 on the desktop tier, 3 on mobile / low (the builder decides). */
  readonly octaves: number;
}

/** The default layer: fair-weather cumulus, gold-lined, a faint Cloud fringe by the sun. */
export const DEFAULT_CLOUD_PARAMS: CloudParams = {
  coverage: 0.42,
  softness: 0.08,
  scale: 1.1,
  opacity: 0.92,
  litColour: [0.92, 0.86, 0.76],
  shadeColour: [0.36, 0.36, 0.48],
  rimColour: [0.9, 0.6, 0.3],
  iridescence: 0.8,
  drift: [0.004, 0.0015],
  octaves: 4,
};

/** The three anchors of The Great Cloud-iridescence (§3.5: "faint cyan→magenta→gold"). */
export const CLOUD_IRIDESCENCE: readonly [
  readonly [number, number, number],
  readonly [number, number, number],
  readonly [number, number, number],
] = [
  [0.55, 0.95, 1.0],
  [1.0, 0.55, 0.95],
  [1.0, 0.85, 0.45],
];

const fract = (v: number): number => v - Math.floor(v);
const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));
const smooth = (e0: number, e1: number, x: number): number => {
  const t = clamp01((x - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
};
const mixN = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Hoskins hash12 — the same sin-free hash as the ground field (groundMaterial.ts). */
export function cloudHash(x: number, y: number): number {
  let p0 = fract(x * 0.1031);
  let p1 = fract(y * 0.1031);
  let p2 = fract(x * 0.1031);
  const d = p0 * (p1 + 33.33) + p1 * (p2 + 33.33) + p2 * (p0 + 33.33);
  p0 += d;
  p1 += d;
  p2 += d;
  return fract((p0 + p1) * p2);
}

/** One value-noise octave (the ground's `groundNoise`). */
export function cloudNoise(x: number, y: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  return mixN(
    mixN(cloudHash(ix, iy), cloudHash(ix + 1, iy), ux),
    mixN(cloudHash(ix, iy + 1), cloudHash(ix + 1, iy + 1), ux),
    uy,
  );
}

/** fbm with the ground's per-octave rotation-ish offset, normalised to 0..1. */
export function cloudFbm(x: number, y: number, octaves: number): number {
  let sum = 0;
  let amp = 0.5;
  let norm = 0;
  let px = x;
  let py = y;
  for (let o = 0; o < octaves; o += 1) {
    sum += amp * cloudNoise(px, py);
    norm += amp;
    const nx = 1.6 * px - 1.2 * py + 17;
    const ny = 1.2 * px + 1.6 * py + 17;
    px = nx;
    py = ny;
    amp *= 0.5;
  }
  return sum / norm;
}

/** The iridescence colour at phase `t` (wraps): cyan → magenta → gold → cyan. */
export function iridescenceColour(t: number): Rgb {
  const phase = fract(t) * 3;
  const [c, m, g] = CLOUD_IRIDESCENCE;
  const out: Rgb = [0, 0, 0];
  for (let i = 0; i < 3; i += 1) {
    let v = mixN(c[i]!, m[i]!, clamp01(phase));
    v = mixN(v, g[i]!, clamp01(phase - 1));
    out[i] = mixN(v, c[i]!, clamp01(phase - 2));
  }
  return out;
}

/** One cloud sample: its colour and opacity in `direction`, at layer time `time` (s). */
export interface CloudSample {
  readonly colour: Rgb;
  readonly alpha: number;
  /** The iridescence weight applied (0 = none) — exposed for tests. */
  readonly iridescence: number;
}

/**
 * The cloud layer in `direction` for a unit `sun`. Directions are projected onto a
 * flattened dome (`xz / (y + 0.1)`) so clouds shrink and thin toward the horizon, where the
 * layer fades out (`y` 0.02 → 0.18) into the fog-matched haze — the fog colour stays the
 * clear-sky horizon. Self-shadow: density falling toward the sun lights a cloud, rising
 * darkens it (a second fbm tap offset toward the sun's bearing).
 */
export function cloudSample(
  direction: SkyVector,
  sun: SkyVector,
  clouds: CloudParams,
  time = 0,
): CloudSample {
  const d = normalize(direction);
  const s = normalize(sun);
  const h = Math.max(d.y, 0);
  const fade = smooth(0.02, 0.18, h);
  const none: CloudSample = { colour: [0, 0, 0], alpha: 0, iridescence: 0 };
  if (fade <= 0 || clouds.coverage <= 0) return none;
  const k = clouds.scale / (h + 0.1);
  const px = d.x * k + clouds.drift[0] * time;
  const py = d.z * k + clouds.drift[1] * time;
  const n = cloudFbm(px, py, clouds.octaves);
  const lo = 1 - clouds.coverage;
  const cover = smooth(lo - clouds.softness, lo + clouds.softness, n);
  if (cover <= 0) return none;
  const sl = Math.hypot(s.x, s.z) || 1;
  const n2 = cloudFbm(px + (s.x / sl) * 0.6, py + (s.z / sl) * 0.6, clouds.octaves);
  const lit = smooth(0.35, 0.65, clamp01(0.5 + (n - n2) * 4));
  const sunDot = Math.max(d.x * s.x + d.y * s.y + d.z * s.z, 0);
  const edge = cover * (1 - cover) * 4;
  const rim = Math.pow(sunDot, 6) * edge;
  const irid = clouds.iridescence * edge * smooth(0.6, 0.97, sunDot);
  const spec = iridescenceColour(n * 2.5 + sunDot * 1.5);
  const colour: Rgb = [0, 0, 0];
  for (let i = 0; i < 3; i += 1) {
    const base =
      mixN(clouds.shadeColour[i]!, clouds.litColour[i]!, lit) + clouds.rimColour[i]! * rim;
    colour[i] = mixN(base, base * (0.45 + spec[i]! * 1.1), irid);
  }
  return { colour, alpha: cover * fade * clouds.opacity, iridescence: irid };
}

/** The whole sky pixel: graded clear sky, clouds composited over it, then the cap. */
export function skyWithClouds(
  direction: SkyVector,
  sun: SkyVector,
  params: SkyParams,
  clouds: CloudParams | null,
  time = 0,
): Rgb {
  const base = skyColourGrade(preethamRadiance(direction, sun, params), params);
  if (!clouds) return skyCap(base, params);
  const c = cloudSample(direction, sun, clouds, time);
  return skyCap(base.map((v, i) => mixN(v, c.colour[i]!, c.alpha)) as Rgb, params);
}

/** Linear → sRGB transfer (IEC 61966-2-1), clamped to [0, 1]. */
function linearToSrgb(c: number): number {
  const v = Math.min(1, Math.max(0, c));
  return v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
}

/**
 * A scene-linear colour as the sRGB hex three's `Color` decodes back to the same
 * linear value (`ColorManagement` is on: `new Color('#…')` is sRGB → linear). So a
 * fog built from this hex adds exactly the sky's horizon radiance to distant
 * geometry, and both pass through the same tone mapping.
 */
export function linearToHex(rgb: readonly [number, number, number]): string {
  return `#${rgb
    .map((c) =>
      Math.round(linearToSrgb(c) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/** The fog colour that agrees with the sky: the horizon radiance as an sRGB hex. */
export function skyHorizonHex(sun: SkyVector, params: SkyParams): string {
  return linearToHex(horizonRadiance(sun, params));
}
